// /coaching-Page: Zustand A (Subscribe) oder Zustand B (Active).
// Liest URL ?success=true und refetcht nach kurzem Wait fuer Webhook-Propagation.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import { useEvePageContextStore } from '@/stores/eve-page-context-store';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import {
  createCheckoutSession,
  fetchCoachingMe,
  openCustomerPortal,
  type CoachingMeResponse,
  type CoachingPlan,
} from '@/lib/coaching-api';
import { SubscribeCards } from '@/components/coaching/subscribe-cards';
import { ActiveCoachingView } from '@/components/coaching/active-coaching-view';

// Aus Welle 2 Setup-Script — Cal.com-URL kommt als Vite-Env oder als API-Antwort.
// Pragmatisch: hardcoded mit Doc-Username als Fallback.
const CALCOM_BOOKING_URL = import.meta.env.VITE_CALCOM_BOOKING_URL
  ?? 'https://cal.com/upro-capital/upro-coaching-60min';

export function CoachingPage() {
  const accessToken = useAuthStore(selectAccessToken);
  const [data, setData] = useState<CoachingMeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const askEve = useEveDrawerStore((s) => s.askEve);
  const setPageContext = useEvePageContextStore((s) => s.setPageContext);
  const clearPageContext = useEvePageContextStore((s) => s.clearPageContext);

  async function reload() {
    if (!accessToken) return;
    setLoading(true);
    try {
      const res = await fetchCoachingMe(accessToken);
      setData(res);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Nach erfolgreichem Stripe-Checkout: Webhook-Wait + Refetch
  useEffect(() => {
    if (searchParams.get('success') !== 'true') return;
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 5 && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        if (cancelled) return;
        await reload();
        if (data?.subscription) break;
      }
      setSearchParams((p) => {
        p.delete('success');
        p.delete('session_id');
        return p;
      });
    };
    void poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    setPageContext({
      pageKey: '/coaching',
      title: 'Coaching',
      summary: data?.subscription
        ? `Aktiv: ${data.subscription.plan}, laeuft bis ${data.subscription.period_end}`
        : 'Inaktiv — Subscribe-View',
      data: data ? (data as unknown as Record<string, unknown>) : undefined,
    });
    return () => clearPageContext();
  }, [data, setPageContext, clearPageContext]);

  async function handleSubscribe(plan: CoachingPlan) {
    if (!accessToken) return;
    const res = await createCheckoutSession(accessToken, plan);
    window.location.href = res.url;
  }

  async function handlePortal() {
    if (!accessToken) return;
    const res = await openCustomerPortal(accessToken);
    window.location.href = res.url;
  }

  const hasActive = !!data?.subscription && data.subscription.status === 'active';

  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="mx-auto max-w-5xl space-y-4">
        <header className="border-b border-white/5 pb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="size-5 text-amber-300" />
            <h1 className="text-xl font-bold">Coaching</h1>
          </div>
          <p className="mt-0.5 text-[12px] text-neutral-500">
            1:1 Ausbildung mit Doc — 3x pro Woche a 1h. Mindest-Laufzeit 3 Monate.
          </p>
        </header>

        {loading ? (
          <div className="py-8 text-center text-[12px] text-neutral-500">
            <Loader2 className="mx-auto mb-2 size-4 animate-spin" />
            Lade Coaching-Status…
          </div>
        ) : error ? (
          <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
            {error}
          </div>
        ) : hasActive && data ? (
          <ActiveCoachingView
            data={data}
            calcomBookingUrl={CALCOM_BOOKING_URL}
            onOpenPortal={handlePortal}
          />
        ) : (
          <>
            <section className="glass-strong rounded-2xl border border-white/5 p-5">
              <h2 className="mb-2 text-[13px] font-bold text-amber-300">Was ist das Coaching?</h2>
              <p className="text-[12px] leading-relaxed text-neutral-300">
                Persoenliche 1:1-Ausbildung. Wir gehen deine Trades durch, bauen dein Setup,
                arbeiten an Risk-Management und Mindset. Drei Sessions pro Woche, 60 Minuten,
                strukturiert ueber mindestens drei Monate — Traden lernt man nicht in vier Wochen.
              </p>
            </section>
            <SubscribeCards onSubscribe={handleSubscribe} />
            <section className="rounded-2xl border border-amber-500/15 bg-amber-500/[0.04] p-4">
              <p className="text-[12px] text-neutral-300">
                Du hast schon ein Abo ueber Gumroad?{' '}
                <button
                  type="button"
                  onClick={() =>
                    askEve(
                      'Ich habe ueber Gumroad ein Coaching-Abo. Bitte schalte mein Konto fuer Coaching frei.',
                      { displayText: 'Coaching-Freischaltung anfragen' },
                    )
                  }
                  className="font-bold text-amber-300 underline hover:text-amber-200"
                >
                  Sag mir Bescheid
                </button>
                , dann schalte ich dich frei.
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
